import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import {
  RefreshCw,
  Activity,
  Database,
  Server,
  Wifi,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Zap,
  HardDrive,
  Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OwnerAdminSystemHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/system/health`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setHealth(data);
      } else {
        toast.error('Failed to fetch system health');
      }
    } catch (error) {
      console.error('Failed to fetch health:', error);
      toast.error('Failed to fetch system health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle2 className="h-5 w-5 text-emerald-400" />;
      case 'degraded':
        return <AlertTriangle className="h-5 w-5 text-yellow-400" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-400" />;
      default:
        return <Activity className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400';
      case 'degraded':
        return 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400';
      case 'error':
        return 'bg-red-500/10 border-red-500/30 text-red-400';
      default:
        return 'bg-muted0/10 border-white/10 text-muted-foreground';
    }
  };

  if (loading && !health) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48 bg-white/10" />
          <Skeleton className="h-10 w-24 bg-white/10" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="bg-card border-white/15">
              <CardContent className="pt-6">
                <Skeleton className="h-20 w-full bg-white/10" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">System Health</h1>
          <p className="text-muted-foreground">Monitor system status and performance</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            Last updated: {health?.timestamp ? new Date(health.timestamp).toLocaleTimeString() : '-'}
          </span>
          <Button 
            variant="outline" 
            onClick={fetchHealth} 
            disabled={loading}
            className="border-white/20 text-muted-foreground/80"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overall Status */}
      <Card className={`border ${getStatusColor(health?.status)}`}>
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {getStatusIcon(health?.status)}
              <div>
                <h2 className="text-xl font-bold text-white">
                  {health?.status === 'healthy' ? 'All Systems Operational' :
                   health?.status === 'degraded' ? 'Degraded Performance' :
                   'System Issues Detected'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {health?.metrics?.errors_24h || 0} errors in the last 24 hours
                </p>
              </div>
            </div>
            <Badge className={getStatusColor(health?.status)}>
              {health?.status?.toUpperCase() || 'UNKNOWN'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Service Status */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* API Status */}
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Server className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-medium text-white">API Server</h3>
                  <p className="text-sm text-muted-foreground">REST API endpoints</p>
                </div>
              </div>
              {getStatusIcon(health?.services?.api || 'healthy')}
            </div>
            <div className="mt-4 pt-4 border-t border-white/15">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <span className="text-emerald-400 capitalize">{health?.services?.api || 'Healthy'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Database Status */}
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Database className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-medium text-white">Database</h3>
                  <p className="text-sm text-muted-foreground">MongoDB</p>
                </div>
              </div>
              {getStatusIcon(health?.services?.database || 'healthy')}
            </div>
            <div className="mt-4 pt-4 border-t border-white/15">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <span className="text-emerald-400 capitalize">{health?.services?.database || 'Healthy'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Sessions */}
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-500/10">
                  <Users className="h-5 w-5 text-violet-400" />
                </div>
                <div>
                  <h3 className="font-medium text-white">Active Sessions</h3>
                  <p className="text-sm text-muted-foreground">User connections</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-white">
                {health?.metrics?.active_sessions || 0}
              </span>
            </div>
            <div className="mt-4 pt-4 border-t border-white/15">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Current active</span>
                <span className="text-muted-foreground/80">{health?.metrics?.active_sessions || 0} sessions</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Errors */}
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <h3 className="font-medium text-white">Errors (24h)</h3>
                  <p className="text-sm text-muted-foreground">System errors</p>
                </div>
              </div>
              <span className={`text-2xl font-bold ${health?.metrics?.errors_24h > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {health?.metrics?.errors_24h || 0}
              </span>
            </div>
            <div className="mt-4 pt-4 border-t border-white/15">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Last 24 hours</span>
                <span className={health?.metrics?.errors_24h > 0 ? 'text-red-400' : 'text-emerald-400'}>
                  {health?.metrics?.errors_24h || 0} errors
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Uptime */}
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Zap className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-medium text-white">Uptime</h3>
                  <p className="text-sm text-muted-foreground">Service availability</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-emerald-400">99.9%</span>
            </div>
            <div className="mt-4 pt-4 border-t border-white/15">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Last 30 days</span>
                <span className="text-emerald-400">99.9% uptime</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Response Time */}
        <Card className="bg-card border-white/15">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/10">
                  <Clock className="h-5 w-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="font-medium text-white">Response Time</h3>
                  <p className="text-sm text-muted-foreground">Average latency</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-white">~150ms</span>
            </div>
            <div className="mt-4 pt-4 border-t border-white/15">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Average</span>
                <span className="text-emerald-400">Good</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
